import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, Zap, Shield, Download } from 'lucide-react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import Button from '../components/ui/Button';
import Icon from '../components/ui/Icon';
import { useTheme } from '../contexts/ThemeContext';

/**
 * Компонент AboutPage - страница "О проекте"
 */
const AboutPage = () => {
  const { darkMode, toggleDarkMode } = useTheme();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white transition-colors duration-200">
      {/* Фоновый узор */}
      <div className="fixed inset-0 bg-[radial-gradient(#e5e7eb_1px,transparent_1px)] dark:bg-[radial-gradient(#1f2937_1px,transparent_1px)] [background-size:20px_20px] opacity-20 pointer-events-none"></div>

      {/* Навигационная панель */}
      <Header darkMode={darkMode} toggleDarkMode={toggleDarkMode} />

      {/* Основное содержимое */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Hero секция */}
        <section className="mb-16 text-center">
          <h1 className="text-4xl sm:text-5xl font-bold mb-4">
            О проекте <span className="text-indigo-600 dark:text-indigo-400">Gensio</span>
          </h1>
          <p className="text-lg sm:text-xl text-gray-700 dark:text-gray-300 max-w-2xl mx-auto">
            Gensio — это бесплатная платформа, созданная для того, чтобы облегчить вашу жизнь.
            Мы собрали более 300 удобных инструментов для расчетов, конвертации, генерации
            данных и многого другого. Наша миссия — дать каждому быстрый доступ к надежным
            решениям в одном месте.
          </p>
        </section>

        {/* Ценности проекта */}
        <section className="mb-16">
          <h2 className="text-2xl font-semibold text-center mb-8">Наши ценности</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-100 dark:border-gray-700 text-center">
              <div className="flex items-center justify-center mb-4">
                <Icon name="zap" size="xl" className="text-indigo-500 dark:text-indigo-400" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Скорость</h3>
              <p className="text-gray-600 dark:text-gray-400">
                Мгновенный доступ и моментальные результаты без задержек.
              </p>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-100 dark:border-gray-700 text-center">
              <div className="flex items-center justify-center mb-4">
                <Icon name="shield" size="xl" className="text-green-500 dark:text-green-400" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Надежность</h3>
              <p className="text-gray-600 dark:text-gray-400">
                Проверенные алгоритмы для корректных и точных вычислений.
              </p>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-100 dark:border-gray-700 text-center">
              <div className="flex items-center justify-center mb-4">
                <Icon name="users" size="xl" className="text-purple-500 dark:text-purple-400" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Сообщество</h3>
              <p className="text-gray-600 dark:text-gray-400">
                Более миллиона пользователей уже доверяют нашим инструментам.
              </p>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-100 dark:border-gray-700 text-center">
              <div className="flex items-center justify-center mb-4">
                <Icon name="download" size="xl" className="text-cyan-500 dark:text-cyan-400" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Доступность</h3>
              <p className="text-gray-600 dark:text-gray-400">
                Полностью бесплатно и без регистрации — все функции открыты.
              </p>
            </div>
          </div>
        </section>

        {/* Команда проекта */}
        <section className="mb-16">
          <h2 className="text-2xl font-semibold text-center mb-8">Наша команда</h2>
          <p className="text-center text-gray-700 dark:text-gray-300 max-w-xl mx-auto mb-8">
            За проектом стоит небольшая, но сплоченная команда разработчиков и дизайнеров,
            увлеченных созданием полезных инструментов для повседневных задач.
          </p>
          {/* Здесь можно добавить карточки участников команды */}
        </section>

        {/* CTA секция */}
        <section className="text-center">
          <h2 className="text-3xl font-bold mb-4">
            Готовы начать?
          </h2>
          <p className="text-lg text-gray-700 dark:text-gray-300 mb-8">
            Перейдите к популярным инструментам и оцените преимущества Gensio сами.
          </p>
          <Button
            primary
            className="px-8 py-4 text-lg"
            onClick={() => navigate('/category/popular')}
          >
            Популярные инструменты
          </Button>
        </section>
      </main>

      {/* Футер */}
      <Footer />
    </div>
  );
};

export default AboutPage;
